import AltContainer from 'alt-container';
import connectToStores from 'alt-utils/lib/connectToStores';
import immutable from 'immutable';
import React from 'react';
import ConstValues from 'utils/const';
import AuthStore from 'stores/AuthStore';
import SearchFieldsStore from 'stores/search/SearchFieldsStore';
import SearchStore from 'stores/SearchStore';
import TrendPivotChartStore from 'stores/calls/trend/TrendPivotChartStore';
import whitelistMap from 'utils/const/searchWidgetWhitelists';
import {SEARCH_FIELD_TYPES} from 'utils/const';
import {SEARCH_FIELD_WIDGET_TYPES} from 'utils/const';
import SearchWidgets from 'components/pure/search/SearchWidgets';

const minNumberOfSearchResultsForSearchWidgetsToShow = 2;

/**
 * `SearchWidgetsContainer` is a `AltContainer` component for all the widgets
 * in the sidebar below the tab with "Saved Searches"
 */
class SearchWidgetsContainer extends React.Component {
  static getStores() {
    return [AuthStore, SearchFieldsStore, SearchStore, TrendPivotChartStore];
  }

  static getPropsFromStores(props) {
    const state = SearchFieldsStore.getState();
    const searchState = SearchStore.getState();
    const searchResults = searchState.get('searchResults').results;
    const searchResultsCount = searchResults ? searchResults.count() : 0;
    const searchRequestFields = searchState.get('searchFields');
    const user = AuthStore.getState().user;
    const trendState = TrendPivotChartStore.getState();

    // If we were linked here from the Add Calls dialog, only return a subset
    // of search fields
    const whitelist = whitelistMap.get(props.widgetWhitelistKey);
    const whitelistedFields = whitelist
      ? state.get('searchFields').filter(sf => whitelist.includes(sf.name))
      : state.get('searchFields');

    return {
      isTermBasedSearch: immutable.Map(searchRequestFields).count() === 0,
      isSearchingLibraryTags: searchState.get('isSearchingLibraryTags'),
      isSearchingSelf: searchState.get('isSearchingSelf'),
      user: user,
      notEnoughSearchResults: searchResultsCount < minNumberOfSearchResultsForSearchWidgetsToShow,
      searchFields: whitelistedFields,
      searchFieldsState: state.get('searchFieldsState'),
      showTrendChart: trendState.get('showTrendChart'),
    };
  }

  /**
   * Return all the search fields with `type`
   * @param  {string} type A specific type of the search fields, e.g., 'boolean', 'numeric', etc.
   * @return {immutable.List}  A list of `Search.Field` objects whose type is `type`
   */
  getSearchFieldsWithType(type) {
    const searchFields = this.props.searchFields.filter(sf => {
      return sf.type === type;
    });
    return searchFields.map(sf => Object.assign(sf, {widgetType: sf.type}));
  }

  /**
   * Return an immutable.Map object that maps `searchField.name` to its state
   * @return {immutable.Map} an `immutable.Map` object that maps `searchField.name`
   *                            to states
   */
  getSearchFieldsStateWithType(type) {
    const searchFieldsState = this.props.searchFieldsState.filter(state => {
      return state.get && state.get('type') === type;
    });
    return searchFieldsState;
  }

  /**
   * Return the toggled state of a search widget with `type`
   * @param  {string} type Type of the search widget
   * @return {boolean}      true / false
   */
  getSearchWidgetToggleState(type) {
    return this.props.searchFieldsState.get(type);
  }

  /**
   * Return all the search fields with `type`
   * @param  {Array} types A list specific type of the search fields, e.g., 'boolean', 'numeric', etc.
   * @return {immutable.List}  A list of `searchField.type`
   */
  getSearchFieldsWithTypes(types) {
    const filteredSearchFields = this.props.searchFields.filter((sf, idx) => {
      return (
        types.includes(sf.type) &&
        !ConstValues.comboboxElasticsearchNames.includes(sf.name) &&
        !ConstValues.radioButtonsElasticsearchNames.includes(sf.name) &&
        !ConstValues.keywordTopicElasticsearchNames.includes(sf.name)
      );
    });
    return filteredSearchFields.map(sf => Object.assign(sf, {widgetType: sf.type}));
  }

  /**
   * Return an immutable.Map object that maps `searchField.name` to its state
   * @param {immutable.List} types A list of `searchField.type`
   */
  getSearchFieldsStateWithTypes(types) {
    return this.props.searchFieldsState.filter((state, key) => {
      let stateType;
      if (typeof state === SEARCH_FIELD_TYPES.BOOLEAN) {
        stateType = state;
      } else {
        stateType = state.type || state.get('type');
      }
      return types.includes(stateType);
    });
  }

  getComboboxSearchFields() {
    const filteredSearchFields = this.props.searchFields.filter(sf => {
      return ConstValues.comboboxElasticsearchNames.includes(sf.name);
    });
    return filteredSearchFields.map(sf =>
      Object.assign(sf, {widgetType: SEARCH_FIELD_WIDGET_TYPES.COMBO_BOX})
    );
  }

  getRadioButtonsSearchFields() {
    const radioButtonsIncludingVoting = this.props.searchFields.filter(sf => {
      // Return true when `sf.name === 'Voting'`, a.k.a., the voting search field
      return ConstValues.radioButtonsElasticsearchNames.includes(sf.name);
    });
    return radioButtonsIncludingVoting.map(sf =>
      Object.assign(sf, {widgetType: SEARCH_FIELD_WIDGET_TYPES.RADIO_BUTTON})
    );
  }

  getKeywordTopicSearchFields() {
    const filteredSearchFields = this.props.searchFields.filter(sf =>
      ConstValues.keywordTopicElasticsearchNames.includes(sf.name)
    );
    return filteredSearchFields.map(sf =>
      Object.assign(sf, {widgetType: SEARCH_FIELD_WIDGET_TYPES.KEYWORD_TOPIC})
    );
  }

  render() {
    return (
      <AltContainer
        inject={{
          booleanSearchFields: props => {
            return this.getSearchFieldsWithType(SEARCH_FIELD_TYPES.BOOLEAN);
          },
          booleanSearchFieldsState: props => {
            return this.getSearchFieldsStateWithType(SEARCH_FIELD_TYPES.BOOLEAN);
          },
          booleanSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.booleanWidgetType);
          },
          numericSearchFields: props => {
            return this.getSearchFieldsWithType(SEARCH_FIELD_TYPES.NUMERIC);
          },
          numericSearchFieldsState: props => {
            return this.getSearchFieldsStateWithType(SEARCH_FIELD_TYPES.NUMERIC);
          },
          numericSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.numericWidgetType);
          },
          datetimeSearchFields: props => {
            return this.getSearchFieldsWithType(SEARCH_FIELD_TYPES.DATETIME);
          },
          datetimeSearchFieldsState: props => {
            return this.getSearchFieldsStateWithType(SEARCH_FIELD_TYPES.DATETIME);
          },
          datetimeSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.datetimeWidgetType);
          },
          enumSearchFields: props => {
            return this.getSearchFieldsWithType(SEARCH_FIELD_TYPES.ENUM);
          },
          enumSearchFieldsState: props => {
            return this.getSearchFieldsStateWithType(SEARCH_FIELD_TYPES.ENUM);
          },
          enumSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.enumWidgetType);
          },
          stringSearchFields: props => {
            return this.getSearchFieldsWithTypes(
              immutable.List([SEARCH_FIELD_TYPES.INFINITE_STRING, SEARCH_FIELD_TYPES.FINITE_STRING])
            );
          },
          stringSearchFieldsState: props => {
            return this.getSearchFieldsStateWithTypes(
              immutable.List([SEARCH_FIELD_TYPES.INFINITE_STRING, SEARCH_FIELD_TYPES.FINITE_STRING])
            );
          },
          stringSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.stringWidgetType);
          },
          comboboxSearchFields: props => {
            return this.getComboboxSearchFields();
          },
          comboboxSearchFieldsState: props => {
            return this.props.searchFieldsState.get(ConstValues.comboboxWidgetTypeForCaller);
          },
          comboboxSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.comboboxWidgetType);
          },
          radioButtonsSearchFields: props => {
            return this.getRadioButtonsSearchFields();
          },
          radioButtonsSearchFieldsState: props => {
            return this.props.searchFieldsState.get(ConstValues.radioButtonsWidgetTypeForVoting);
          },
          radioButtonsSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.radioButtonsWidgetTypeForVoting);
          },
          keywordTopicSearchFields: props => {
            return this.getKeywordTopicSearchFields();
          },
          keywordTopicSearchFieldsState: props => {
            return this.props.searchFieldsState.get(ConstValues.keywordTopicWidgetType);
          },
          keywordTopicSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.keywordTopicWidgetType);
          },
          phoneSearchFields: props => {
            return this.getSearchFieldsWithType(SEARCH_FIELD_TYPES.PHONE);
          },
          phoneSearchFieldsState: props => {
            return this.props.searchFieldsState.get(ConstValues.phoneWidgetType);
          },
          phoneSearchFieldsToggled: props => {
            return this.getSearchWidgetToggleState(ConstValues.phoneWidgetType);
          },
        }}>
        <SearchWidgets {...this.props} />
      </AltContainer>
    );
  }
}

export default connectToStores(SearchWidgetsContainer);
